import React, { Suspense } from 'react';
import { Router, useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { i18n } from '@lingui/core';

import {
  usePublicSetQuery,
  PublicEWebinarFragment,
  CookieBannerMode,
} from '$/GraphQL/schema/schema';
import Loading from '$/components/Loading';
import { isMatchPath } from '$/utils/hasPermission';
import { css } from '@emotion/react';
import { FBPixelContextProvider } from '$/utils/useFBPixel';
import { useStaticSetContext } from '$/components/pages/webinar';
import { GdprContext } from '$/components/GdprCookie/GdprContext';
import I18nLoader from '$/components/I18nLoader';
import NoWebinar from '$/components/pages/404/NoWebinar';
import { PageName, PageMode } from '$/components/ui/Input/PageBuilder.types';
import { WidgetEventType } from '$/widget/src/interface';
import { logger } from '$/utils/logger';
import { useServerContext } from '$/hooks/useServerDateNow';
import { importWithRetry } from '$/utils/import';
import { Helmet } from 'react-helmet';
import { LazyRoute } from '$/components/LazyRoute';
import config from '$/config';

const RegistrationPage = React.lazy(
  () => import('$/sections/Home/WebinarEditor/RegistrationPages/RegistrationPage/RegistrationPage')
);

const RegistrationEmbedForm = React.lazy(
  () => import('$/sections/PublishedWebinar/Registration/RegistrationEmbedForm')
);

const RegistrationForm = React.lazy(
  () => import('$/sections/PublishedWebinar/Registration/RegistrationForm')
);

const ThankYouPage = React.lazy(
  () => import('$/sections/Home/WebinarEditor/RegistrationPages/ThankYouPage/ThankYouPage')
);

export const RegistrationPagesStylesAndLang: React.FC<{
  ewebinar: PublicEWebinarFragment;
  ssrBuildTime?: boolean;
  children: React.ReactNode;
}> = ({ ssrBuildTime, ewebinar, children }) => {
  const needToLoadLang = React.useRef(ssrBuildTime);
  if (needToLoadLang.current) {
    const lang = ewebinar.viewerRoomSettings.language ?? 'en';
    const { messages } = require(`../../../locales/${lang}/messages`);
    i18n.load(lang, messages);
    i18n.activate(lang);
    needToLoadLang.current = false;
  }

  return (
    <div
      css={css`
        width: 100%;
        flex: 0 0 100%;
        & a {
          text-decoration: underline;
          color: #007bff;
          &:visited {
            color: #551a8b;
          }
          &:hover {
            text-decoration: underline;
          }
        }
      `}
    >
      <I18nLoader langCode={ewebinar.viewerRoomSettings.language}>{children}</I18nLoader>
    </div>
  );
};

const WidgetNoSession: React.FC = () => {
  React.useEffect(() => {
    try {
      if (!window.eWebinarWidget) {
        const { createWidget } = require('$/widget/src/widget');
        window.eWebinarWidget = createWidget();
      }
      window.xprops?.emit({
        event: WidgetEventType.HasNoSession,
      });
    } catch {}
  }, []);

  return null;
};

const Register: React.FC<{ setId?: string }> = ({ setId }) => {
  const { staticset, location } = useStaticSetContext();
  const { isAcceptedCookies } = React.useContext(GdprContext);
  const pathname = useLocation().pathname ?? location?.pathname;
  const renderWidgetPopupPage = pathname.match(/^\/webinar\/([a-z-0-9]*)\/register\/widget/);
  const renderFastFormPage = pathname.match(/^\/webinar\/([a-z-0-9]*)\/register\/fast-form/);
  const renderingWidget = renderWidgetPopupPage || renderFastFormPage;
  const setQuery = usePublicSetQuery({
    variables: { id: setId! },
    skip: config.isSSR,
  });
  const setLoaded = setQuery.called && !setQuery.loading && !config.isSSR;
  const team = setQuery?.data?.publicSet?.publicWebinar?.team;
  const { setServerDate } = useServerContext();

  React.useEffect(() => {
    if (!setQuery.data?.publicSet?.now) return;
    setServerDate(new Date(setQuery.data?.publicSet?.now));
  }, [setServerDate, setQuery.data?.publicSet?.now]);

  React.useEffect(() => {
    if (!team?.domain || renderingWidget) {
      return;
    }

    const { isDomainVerified, domain } = team;

    if (!isDomainVerified || domain.length === 0) {
      return;
    }

    const url = new URL(window.location.href);

    if (url.hostname === domain) {
      return;
    }

    url.hostname = domain;
    url.port = ''; // verified domain will have 443 port for sure

    navigate(url.href);
  }, [renderingWidget, team, team?.domain, team?.isDomainVerified]);

  React.useEffect(() => {
    const formRoute = '/webinar/:setId/register/form';

    if (isMatchPath(formRoute, pathname)) {
      document.body.classList.add('RegistrationFormModalOpen');
    } else {
      document.body.classList.remove('RegistrationFormModalOpen');
    }
  }, [pathname]);

  const set = staticset ?? setQuery?.data?.publicSet;
  const ewebinar = set?.publicWebinar;

  React.useEffect(() => {
    // This loads the font when we're running React
    // We also need Helmet to include it for SSR Rendered pages

    if (!renderingWidget || !ewebinar) return;

    async function loadFontIntoIFrame(): Promise<void> {
      if (!ewebinar) return;

      if (ewebinar.fontCssUrl && ewebinar.font) {
        const WebFontLoader = await importWithRetry(() => import('webfontloader'));
        WebFontLoader.load({
          custom: {
            families: [ewebinar.font],
            urls: [ewebinar.fontCssUrl],
          },
        });
      }
    }

    void loadFontIntoIFrame();
  }, [ewebinar, ewebinar?.font, ewebinar?.fontCssUrl, renderingWidget]);

  if (!setLoaded && !staticset) {
    return <Loading query={setQuery} />;
  }

  if (setLoaded && (!setQuery.data?.publicSet || !setQuery.data.publicSet.publicWebinar)) {
    logger.error('ERROR Getting public set for registration page', { error: setQuery.error });

    return (
      <>
        <I18nLoader langCode='en'>
          <NoWebinar
            webinarId={setId}
            teamId={team?.id}
            extensions={
              setQuery.error?.graphQLErrors && setQuery.error.graphQLErrors[0]
                ? setQuery.error.graphQLErrors[0].extensions
                : { code: 'NOT_FOUND' }
            }
          />
        </I18nLoader>
        <Router>
          <WidgetNoSession path='/register/widget' />
        </Router>
      </>
    );
  }

  if (!set || !ewebinar) {
    // Set was unpublished and we found out via page-data.json having undefined set
    return (
      <NoWebinar
        webinarId={setId}
        teamId={team?.id}
        extensions={{
          code: 'NOT_PUBLISHED',
        }}
      />
    );
  }

  if (renderingWidget) {
    return (
      <div
        id='fontLoader'
        css={css`
          font-family: ${ewebinar.font ?? 'DM Sans'}, 'DM Sans', sans-serif;
        `}
      >
        {ewebinar.fontCssUrl && (
          <Helmet>
            <link href={ewebinar.fontCssUrl} rel='stylesheet' />
          </Helmet>
        )}
        {renderWidgetPopupPage ? (
          <Suspense>
            <RegistrationForm
              ewebinar={ewebinar}
              set={set}
              pixelID={setQuery?.data?.publicSet.setSettings.pixelID ?? undefined}
              isWidget
            />
          </Suspense>
        ) : (
          <Suspense>
            <RegistrationEmbedForm ewebinar={ewebinar} set={set} />
          </Suspense>
        )}
      </div>
    );
  }

  return (
    <RegistrationPagesStylesAndLang ssrBuildTime={Boolean(staticset)} ewebinar={ewebinar}>
      <FBPixelContextProvider
        pixelID={setQuery?.data?.publicSet.setSettings.pixelID}
        isAcceptedCookies={
          setQuery?.data?.publicSet.team.settings.gdpr.bannerMode === CookieBannerMode.Off ||
          isAcceptedCookies
        }
      >
        <Router>
          <LazyRoute
            Component={ThankYouPage}
            path='/register/thankyou/*attendeeId'
            set={set}
            ewebinar={ewebinar}
            mode={PageMode.public}
            pagename={PageName.thankyouPage}
            fallback={<Loading />}
          />

          <LazyRoute
            Component={RegistrationPage}
            default
            set={set}
            ewebinar={ewebinar}
            mode={PageMode.public}
            hasPreRendered={Boolean(staticset)}
            pagename={
              pathname.includes('form') ? PageName.registrationForm : PageName.registrationPage
            }
            isPublic={true}
            fallback={<Loading />}
          />
        </Router>
      </FBPixelContextProvider>
    </RegistrationPagesStylesAndLang>
  );
};

export default Register;
